<script lang="ts" setup>
  import dayjs from 'dayjs';
  import { onMounted, ref } from 'vue';
  import { useApi } from '@/composables/use-api.ts';
  import {
    printWorkStartDtOrWorkEndDt,
    secondsToHoursAndMinutes,
  } from '@/utils/item-printer.ts';
  import {
    calExtraWorkTime,
    calHolidayWorkTime,
    calNightWorkTime,
  } from '@/services/attendance-svc.ts';
  import { loadWithStatusAndErrorHandleAsync } from '@/services/loading-svc.ts';
  import { AttendanceWithApprovalsDto } from '@/services/api/attendance-day-api.ts';
  import { EmployeeWithOrganizationDto } from '@/services/api/employee-api.ts';

  const props = defineProps<{
    empNo: number;
    targetDate: string; // YYYY-MM-DD
  }>();

  const { company, attendance, secom } = useApi();

  const loading = ref<boolean>(false);
  const empWithOrg = ref<EmployeeWithOrganizationDto>();
  const item = ref<AttendanceWithApprovalsDto>();
  const comments = ref<string[]>([]);
  const taggingStartDt = ref('');
  const taggingEndDt = ref('');

  async function refreshAsync() {
    await loadWithStatusAndErrorHandleAsync(
      (status) => {
        loading.value = status;
      },
      async () => {
        empWithOrg.value = await company.employee.getAsync(props.empNo);

        item.value = await attendance.day.getWithApprovalsAsync(
          props.targetDate,
          props.empNo
        );
        if (item.value?.comment) {
          comments.value = JSON.parse(item.value.comment);
        }

        const summary = await secom.getSummaryAsync(
          props.targetDate,
          props.empNo
        );
        taggingStartDt.value = dayjs(summary.firstDt).format('HH:mm');
        taggingEndDt.value = dayjs(summary.lastDt).format('HH:mm');
      }
    );
  }

  onMounted(async () => {
    await refreshAsync();
  });
</script>
<template>
  <DefaultLayout
    title="일 근무 열람"
    go-back>
    <div class="mt-0 mb-0 divider text-lg">
      {{ dayjs(props.targetDate).format('YYYY년 M월 D일') }}
    </div>

    <skeleton
      v-if="loading"
      class="my-5 mb-8" />
    <div
      v-else
      class="pt-3 pb-8 overflow-x-auto">
      <div class="flex px-4 text-sm">
        <div class="flex-auto">신청자</div>
        <div class="text-right">
          <p>
            {{ `${empWithOrg?.organization.name} ${empWithOrg?.name}` }}
          </p>
        </div>
      </div>
      <div class="m-1.5 divider divider-h" />
      <div class="flex px-4 text-sm">
        <div class="flex-auto">일일 근로 시간</div>
        <div class="text-right">
          {{ secondsToHoursAndMinutes(item?.workTime ?? 0) }}
        </div>
      </div>
      <div class="m-1.5 divider divider-h" />
      <div class="flex px-4 text-sm">
        <div class="flex-auto">근무 시작 시간</div>
        <div class="text-right">
          {{ printWorkStartDtOrWorkEndDt(item?.workStartDt) }}
        </div>
      </div>
      <div class="m-1.5 divider divider-h" />
      <div class="flex px-4 text-sm">
        <div class="flex-auto">근무 종료 시간</div>
        <div class="text-right">
          {{ printWorkStartDtOrWorkEndDt(item?.workEndDt) }}
        </div>
      </div>
      <div class="m-1.5 divider divider-h" />
      <div class="flex px-4 text-sm">
        <div class="flex-auto">최초 태깅 시간</div>
        <div class="text-right">
          {{ taggingStartDt }}
        </div>
      </div>
      <div class="m-1.5 divider divider-h" />
      <div class="flex px-4 text-sm">
        <div class="flex-auto">최종 태깅 시간</div>
        <div class="text-right">
          {{ taggingEndDt }}
        </div>
      </div>
      <div class="m-1.5 divider divider-h" />
      <div class="flex px-4 text-sm">
        <div class="flex-auto">휴게 시간</div>
        <div class="text-right">
          {{ secondsToHoursAndMinutes(item?.breakTime ?? 0) }}
        </div>
      </div>
      <div class="m-1.5 divider divider-h" />
      <div class="flex px-4 text-sm">
        <div class="flex-auto">쉬러 간 시간</div>
        <div class="text-right">
          {{ secondsToHoursAndMinutes(item?.selfBreakTime ?? 0) }}
        </div>
      </div>

      <template v-if="item?.holiday == 'N'">
        <div class="m-1.5 divider divider-h" />
        <div
          class="collapse"
          tabindex="0">
          <div class="flex px-4 text-sm">
            <div class="flex-auto">연장근무(실제/승인)</div>
            <div class="text-right link">
              {{ secondsToHoursAndMinutes(calExtraWorkTime(item)) }} /
              {{
                secondsToHoursAndMinutes(
                  (item?.extraWork?.dayTime ?? 0) +
                    (item?.extraWork?.nightTime ?? 0)
                )
              }}
            </div>
          </div>
          <div class="collapse-content pl-6 pr-4">
            <div class="my-5" />
            <div class="flex text-sm">
              <div class="flex-auto">주간 연장 근무</div>
              <div class="text-right">
                {{ secondsToHoursAndMinutes(item?.workExtraDayTime ?? 0) }} /
                {{ secondsToHoursAndMinutes(item?.extraWork?.dayTime ?? 0) }}
              </div>
            </div>
            <div class="my-1 divider divider-h" />
            <div class="flex text-sm">
              <div class="flex-auto">야간 연장 근무</div>
              <div class="text-right">
                {{ secondsToHoursAndMinutes(item?.workExtraNightTime ?? 0) }} /
                {{ secondsToHoursAndMinutes(item?.extraWork?.nightTime ?? 0) }}
              </div>
            </div>
          </div>
        </div>

        <div class="m-1.5 divider divider-h" />
        <div class="flex px-4 text-sm">
          <div class="flex-auto">야간근무(실제/승인)</div>
          <div class="text-right">
            {{ secondsToHoursAndMinutes(calNightWorkTime(item)) }} /
            {{
              secondsToHoursAndMinutes(
                (item?.nightWork?.dayTime ?? 0) +
                  (item?.nightWork?.nightTime ?? 0)
              )
            }}
          </div>
        </div>
      </template>
      <template v-if="item?.holiday == 'Y'">
        <div class="m-1.5 divider divider-h" />
        <div
          class="collapse"
          tabindex="0">
          <div class="flex px-4 text-sm">
            <div class="flex-auto">휴일근무(실제/승인)</div>
            <div class="text-right link">
              {{ secondsToHoursAndMinutes(calHolidayWorkTime(item)) }} /
              {{
                secondsToHoursAndMinutes(
                  (item?.holidayWork?.dayTime ?? 0) +
                    (item?.holidayWork?.nightTime ?? 0)
                )
              }}
            </div>
          </div>
          <div class="collapse-content pl-6 pr-4">
            <div class="my-5" />
            <div class="flex text-sm">
              <div class="flex-auto">주간 휴일 근무</div>
              <div class="text-right">
                {{ secondsToHoursAndMinutes(item?.workDayTime ?? 0) }} /
                {{ secondsToHoursAndMinutes(item?.holidayWork?.dayTime ?? 0) }}
              </div>
            </div>
            <div class="my-1 divider divider-h" />
            <div class="flex text-sm">
              <div class="flex-auto">야간 휴일 근무</div>
              <div class="text-right">
                {{ secondsToHoursAndMinutes(item?.workNightTime ?? 0) }} /
                {{
                  secondsToHoursAndMinutes(item?.holidayWork?.nightTime ?? 0)
                }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-if="comments.length != 0">
        <div class="m-1.5 divider divider-h" />
        <div class="flex px-4 text-sm">
          <div class="flex-auto min-w-fit mr-5">근무 코멘트</div>
          <div class="text-right">
            <div
              v-for="(comment, index) in comments"
              :key="index"
              class="text-right">
              {{ `- ${comment}` }}
            </div>
          </div>
        </div>
      </template>
    </div>

    <div v-if="item">
      <div class="mt-4 mb-0 divider text-center text-sm">타임라인</div>

      <AttendanceTimeline
        :attendance-id="item!.id"
        :date="props.targetDate"
        :emp-no="props.empNo"
        class="pb-4" />
    </div>
  </DefaultLayout>
</template>
<style lang="scss" scoped>
  .divider-h:before {
    height: 0.05rem;
  }

  .divider-h:after {
    height: 0.05rem;
  }
</style>
